/** Placed — the order's identity (order ref and machine ID, the D10 fields: editable
 *  behind their warnings, the order ref's required "Linked bookings" question under it),
 *  then its state, references, account and own tags. Under them: who the order's events
 *  address now — what an account change moves. */
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { Box, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { OrderRecordStage } from '@/api/orderRecord';
import type { ViewSection } from '@/features/records/views/types';

import { IDENTITY_KEYS } from '../registry';

import type { OrderDisplayContext } from './displays';
import { OrderStagePanel } from './OrderStagePanel';

function personName(u: { first_name?: string | null; last_name?: string | null; id: number }): string {
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || `User #${u.id}`;
}

function NotificationUser({ c }: { c: OrderDisplayContext }) {
  const u = c.editContext.notification_user;
  return (
    <Typography
      variant="caption"
      component="p"
      color="text.secondary"
      data-notification-user
      sx={{ m: 0, display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
    >
      <NotificationsNoneOutlinedIcon aria-hidden sx={{ fontSize: 16, flex: 'none' }} />
      <span>
        {u ? (
          <>
            Order events address{' '}
            <Link component={RouterLink} to={`/users/${u.id}`} underline="hover">
              {personName(u)}
            </Link>{' '}
            (#{u.id}).
          </>
        ) : (
          'No one receives the order’s events — it has no account and its primary booking no guest.'
        )}
      </span>
    </Typography>
  );
}

export function PlacedPanel({
  section,
  display,
}: {
  section: ViewSection<OrderRecordStage>;
  display: OrderDisplayContext;
}) {
  return (
    <OrderStagePanel
      section={section}
      display={display}
      groupEditable={(fields) => [
        { id: 'identity', title: 'Identity', layout: 'identity', fields: fields.filter((f) => IDENTITY_KEYS.has(f.key)) },
        { id: 'order', fields: fields.filter((f) => !IDENTITY_KEYS.has(f.key)) },
      ]}
      afterEditable={
        <Box>
          <NotificationUser c={display} />
        </Box>
      }
    />
  );
}
